import React, { useCallback, useRef } from 'react';
import { PlatformEntityData } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder/PlatformEntityData';
import { HighlightMatch } from './../../components/lists/HighlightMatch';
import classNames from 'classnames';
import './SearchResultItem.less';
import { getImageForEntity } from 'utils/ImageUtils';

export interface ISearchResultClickEvent {
  entity: PlatformEntityData;
  target: HTMLLIElement | null;
  clientX?: number;
  clientY?: number;
}

export type ISearchResultClick = (event: ISearchResultClickEvent) => void;

export interface ISearchResultItemProps {
  entity: PlatformEntityData;
  searchText: string;
  isCurrent?: boolean;
  onClick: ISearchResultClick;
  onContextMenu: ISearchResultClick;
}

const getClassName = (isCurrent?: boolean) =>
  classNames('dpc-portal-searchbox-item', {
    'dpc-portal-searchbox-item--current': isCurrent,
  });

export function SearchResultItem({ entity, searchText, onClick, onContextMenu, isCurrent }: ISearchResultItemProps) {
  const element = useRef<HTMLLIElement | null>(null);

  let imageUrl = entity.IconImageUrl
    ? `${virtualPath}${entity.IconImageUrl}`
    : getImageForEntity(entity.Id, entity.Type);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLLIElement, MouseEvent>) =>
      onClick({ entity, target: element.current, clientX: e.clientX, clientY: e.clientY }),
    [entity, onClick]
  );
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLLIElement, MouseEvent>) =>
      onContextMenu({
        entity,
        target: element.current,
        clientX: e.clientX,
        clientY: e.clientY,
      }),
    [entity, onContextMenu]
  );

  return (
    <li
      ref={element}
      className={getClassName(isCurrent)}
      role="menuitem"
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      <img className="dpc-portal-searchbox-item__icon" src={imageUrl} alt={`${entity.ShortTypeName} icon`} />
      <div className="dpc-portal-searchbox-item__title" title={getTitle(entity)}>
        <HighlightMatch searchString={searchText}>{entity.Name}</HighlightMatch>
        {entity.FullPath && <span className="dpc-portal-searchbox-item__description"> [ {entity.FullPath} ]</span>}
      </div>
      <div className="dpc-portal-searchbox-item__type-container">
        <div className="dpc-portal-searchbox-item__type">{entity.ShortTypeName}</div>
      </div>
    </li>
  );
}

function getTitle(entity: PlatformEntityData) {
  return !!entity.FullPath ? `${entity.Name} [${entity.FullPath}]` : entity.Name;
}
